'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ScaleTypeConfig, ScaleBucket } from '../types/session';

// ============================================================================
// Props
// ============================================================================

interface ScaleVisualProps {
  config: ScaleTypeConfig;
  mode: 'static' | 'interactive' | 'segmented-reference';
  score?: number | null;
  requiresMemberInput?: boolean;
  animateEntrance?: boolean;
  /** Associates the interactive slider with a visible question label. */
  ariaLabelledby?: string;
  /**
   * Base data-testid for this instrument's anchor family. Pinflow captures an
   * anchor from the EXACT clicked element (`event.target.getAttribute`), so a
   * container-level id alone never gets recorded — every leaf a member can
   * land on carries its own suffixed anchor: the slider surface gets
   * `<anchorId>`, plus `-track`, `-fill`, `-thumb`/`-thumb-hit`/`-thumb-dot`,
   * `-marker-<value>`, `-min-label`, `-max-label`, `-badge`, `-hint`.
   * Callers with a dynamic prefix part must escape it (testAnchorPart) so the
   * fixed suffixes cannot alias. Absent → no anchors (shared non-form usages).
   */
  anchorId?: string;
  onScoreChange?: (value: number) => void;
  onScoreCommit?: (value: number, source: 'slider' | 'number_tap') => void;
}

// ============================================================================
// Helpers
// ============================================================================

function scoreToPercent(value: number, min: number, max: number): number {
  return ((value - min) / (max - min)) * 100;
}

function percentToScore(pct: number, min: number, max: number, step: number): number {
  const raw = min + (pct / 100) * (max - min);
  const stepped = Math.round(raw / step) * step;
  return Math.max(min, Math.min(max, stepped));
}

function getBucketWithIndex(
  value: number,
  buckets?: ScaleBucket[],
): { bucket: ScaleBucket; index: number } | null {
  if (!buckets) return null;
  const index = buckets.findIndex((b) => value >= b.min && value <= b.max);
  if (index === -1) return null;
  return { bucket: buckets[index], index };
}

// ---------------------------------------------------------------------------
// Fallback color resolution for buckets with missing or empty `color`.
// Keeps scale questions visibly rendered when authored presentation data is
// structurally valid but visually incomplete. No campaign-specific branching.
// ---------------------------------------------------------------------------

const SLUG_COLOR_MAP: Record<string, string> = {
  detractor: '#DC6B6B',
  negative: '#DC6B6B',
  low: '#DC6B6B',
  passive: '#D9A441',
  neutral: '#D9A441',
  mid: '#D9A441',
  medium: '#D9A441',
  promoter: '#5AA878',
  positive: '#5AA878',
  high: '#5AA878',
};

const NEUTRAL_FALLBACK = '#6B7280';

const INDEX_PALETTES: Record<number, string[]> = {
  2: ['#DC6B6B', '#5AA878'],
  3: ['#DC6B6B', '#D9A441', '#5AA878'],
  4: ['#DC6B6B', '#D9A441', '#8FB86B', '#5AA878'],
  5: ['#DC6B6B', '#E3A85A', '#D9A441', '#8FB86B', '#5AA878'],
};

const NEUTRAL_RAMP = ['#94A3B8', '#7C8BA3', '#64748B', '#4F5E75', '#475569'];

export function resolveBucketColor(
  bucket: Pick<ScaleBucket, 'color' | 'slug'>,
  index: number,
  total: number,
): string {
  const authored = typeof bucket.color === 'string' ? bucket.color.trim() : '';
  if (authored.length > 0) return authored;

  const slug = typeof bucket.slug === 'string' ? bucket.slug.trim().toLowerCase() : '';
  if (slug && SLUG_COLOR_MAP[slug]) return SLUG_COLOR_MAP[slug];

  const palette = INDEX_PALETTES[total];
  if (palette) {
    return palette[Math.max(0, Math.min(index, palette.length - 1))];
  }

  if (total <= 0) return NEUTRAL_FALLBACK;
  const rampPos = Math.round((index / Math.max(1, total - 1)) * (NEUTRAL_RAMP.length - 1));
  return NEUTRAL_RAMP[Math.max(0, Math.min(rampPos, NEUTRAL_RAMP.length - 1))];
}

function buildGradientStops(buckets: ScaleBucket[], min: number, max: number): string {
  const range = max - min;
  const total = buckets.length;
  const stops = buckets
    .map((b, i) => {
      const color = resolveBucketColor(b, i, total);
      const startPct = ((b.min - min) / range) * 100;
      const endPct = ((b.max - min) / range) * 100;
      return `${color} ${startPct}%, ${color} ${endPct}%`;
    })
    .join(', ');
  return `linear-gradient(to right, ${stops})`;
}

// ============================================================================
// Component
// ============================================================================

export function ScaleVisual({
  config,
  mode,
  score,
  requiresMemberInput,
  animateEntrance = true,
  ariaLabelledby,
  anchorId,
  onScoreChange,
  onScoreCommit,
}: ScaleVisualProps) {
  const anchor = (suffix: string): string | undefined =>
    anchorId ? `${anchorId}${suffix}` : undefined;
  const { min, max, step = 1, buckets, min_label, max_label } = config;
  const range = max - min;
  const numbers = Array.from({ length: range / step + 1 }, (_, i) => min + i * step);

  // ============================================================================
  // Segmented Reference Mode
  // ============================================================================

  if (mode === 'segmented-reference' && buckets) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
        className="w-full select-none"
      >
        {/* Segment blocks */}
        <div className="flex gap-[3px] px-2">
          {buckets.map((bucket, i) => (
            <div
              key={bucket.label}
              className="h-1.5 rounded-full"
              style={{
                flex: bucket.max - bucket.min + 1,
                backgroundColor: resolveBucketColor(bucket, i, buckets.length),
                opacity: 0.5,
              }}
            />
          ))}
        </div>

        {/* Range labels */}
        <div className="flex gap-[3px] px-2 mt-1">
          {buckets.map((bucket) => (
            <div
              key={bucket.label}
              className="flex justify-center"
              style={{ flex: bucket.max - bucket.min + 1 }}
            >
              <span className="font-body text-[10px] text-[#A8A29E]">
                {bucket.min === bucket.max ? `${bucket.min}` : `${bucket.min}\u2013${bucket.max}`}
              </span>
            </div>
          ))}
        </div>

        {/* Endpoint labels */}
        <div className="flex justify-between px-2 mt-1">
          <span className="font-body text-[11px] text-[#9CA3AF]">{min_label}</span>
          <span className="font-body text-[11px] text-[#9CA3AF]">{max_label}</span>
        </div>
      </motion.div>
    );
  }

  // Local score state for interactive mode
  const [localScore, setLocalScore] = useState<number | null>(score ?? null);
  const [originalScore] = useState<number | null>(score ?? null);
  const [hasPlaced, setHasPlaced] = useState(score != null && !requiresMemberInput);
  const [isDragging, setIsDragging] = useState(false);
  const [markerSettled, setMarkerSettled] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  // Sync external score changes
  useEffect(() => {
    if (score != null && !hasPlaced) {
      setLocalScore(score);
      setHasPlaced(true);
    }
  }, [score, hasPlaced]);

  // Mark marker as settled after spring animation
  useEffect(() => {
    if (hasPlaced && mode === 'interactive') {
      const timer = setTimeout(() => setMarkerSettled(true), 800);
      return () => clearTimeout(timer);
    }
  }, [hasPlaced, mode]);

  const bucketWithIndex = localScore != null ? getBucketWithIndex(localScore, buckets) : null;
  const bucket = bucketWithIndex?.bucket ?? null;
  const bucketCount = buckets?.length ?? 0;
  const bucketColor = bucketWithIndex
    ? resolveBucketColor(bucketWithIndex.bucket, bucketWithIndex.index, bucketCount)
    : null;
  const gradient = buckets ? buildGradientStops(buckets, min, max) : `linear-gradient(to right, #9CA3AF, #6B7280)`;
  const scorePercent = localScore != null ? scoreToPercent(localScore, min, max) : 0;

  const updateScore = useCallback(
    (value: number) => {
      setLocalScore(value);
      if (!hasPlaced) setHasPlaced(true);
      onScoreChange?.(value);
    },
    [hasPlaced, onScoreChange],
  );

  const commitScore = useCallback(
    (value: number, source: 'slider' | 'number_tap') => {
      setLocalScore(value);
      if (!hasPlaced) setHasPlaced(true);
      onScoreCommit?.(value, source);
    },
    [hasPlaced, onScoreCommit],
  );

  // Pointer interaction for dragging on track
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (mode !== 'interactive') return;
      e.preventDefault();
      // Capture on currentTarget (the track), NOT e.target. e.target can be a
      // child (thumb/value bubble/fill) — capturing there routes subsequent
      // pointermove events to the child, which has no move handler, so drag
      // silently dies and the slider feels "stuck". The track owns all three
      // pointer handlers, so it must own the capture.
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);

      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const innerLeft = rect.left + 8;
      const innerWidth = rect.width - 16;
      const pct = Math.max(0, Math.min(100, ((e.clientX - innerLeft) / innerWidth) * 100));
      const val = percentToScore(pct, min, max, step);
      updateScore(val);
    },
    [mode, min, max, step, updateScore],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging || mode !== 'interactive') return;
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const innerLeft = rect.left + 8;
      const innerWidth = rect.width - 16;
      const pct = Math.max(0, Math.min(100, ((e.clientX - innerLeft) / innerWidth) * 100));
      const val = percentToScore(pct, min, max, step);
      updateScore(val);
    },
    [isDragging, mode, min, max, step, updateScore],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      setIsDragging(false);
      // Release on currentTarget — symmetric with handlePointerDown's capture
      // on currentTarget. Releasing on e.target (a child the pointer may have
      // moved over) would not own the capture, leaving it dangling on some
      // browsers. Guard first since the pointer may have started on a child.
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      if (localScore != null) {
        commitScore(localScore, 'slider');
      }
    },
    [isDragging, localScore, commitScore],
  );

  // Keyboard accessibility
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (mode !== 'interactive') return;
      let newVal: number | null = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        newVal = Math.min(max, (localScore ?? min) + step);
        e.preventDefault();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        newVal = Math.max(min, (localScore ?? max) - step);
        e.preventDefault();
      }
      if (e.key === 'Home') {
        newVal = min;
        e.preventDefault();
      }
      if (e.key === 'End') {
        newVal = max;
        e.preventDefault();
      }
      if (newVal != null) {
        updateScore(newVal);
        commitScore(newVal, 'slider');
      }
    },
    [mode, localScore, min, max, step, updateScore, commitScore],
  );

  // Number thinning for small viewports
  const shouldThinNumbers = numbers.length > 7;
  const midpoint = min + Math.floor(range / 2);

  // Determine if we should show a score delta
  const showDelta = originalScore != null && localScore != null && localScore !== originalScore;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
      className="w-full select-none"
    >
     {/* Dimmed wrapper for track + numbers in static mode */}
     <div style={mode === 'static' ? { opacity: 0.55 } : undefined}>
      {/* The slot is ALWAYS rendered at a fixed height so the badge's arrival
          never pushes the track (and everything below it) down: fixed-height
          containers for appearing elements; adjacent content must not bounce. */}
      {mode === 'interactive' && (
        <div className="mb-4 flex h-7 items-center justify-center">
          {hasPlaced && localScore != null && (
          <motion.span
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            data-testid={anchor('-badge')}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-body text-sm font-semibold tabular-nums"
            style={{
              backgroundColor: bucketColor ? `color-mix(in srgb, ${bucketColor} 15%, transparent)` : 'rgba(156,163,175,0.15)',
              color: bucketColor ?? '#6B7280',
            }}
          >
            {showDelta ? (
              <>
                <span className="opacity-50">{originalScore}</span>
                <span className="opacity-50">{'\u2192'}</span>
                <span>{localScore}</span>
              </>
            ) : (
              localScore
            )}
            {/* Bucket label intentionally omitted from badge */}
          </motion.span>
          )}
        </div>
      )}

      {/* Track area.
          The OUTER div is the pressable hit zone: the visible line is only
          6px tall, which made it fiddly to grab. Vertical padding (py-3)
          expands the grab target to ~30px without changing the visual track,
          since the gradient/fill/thumb live in the INNER (trackRef) box and
          are positioned against IT (top:0), so they stay aligned regardless
          of the outer padding. Pointer x-math reads only trackRef's
          left/width, so the larger hit zone never shifts the value mapping. */}
      <div
        data-testid={anchorId}
        className="relative px-2 py-3"
        onPointerDown={mode === 'interactive' ? handlePointerDown : undefined}
        onPointerMove={mode === 'interactive' ? handlePointerMove : undefined}
        onPointerUp={mode === 'interactive' ? handlePointerUp : undefined}
        role={mode === 'interactive' ? 'slider' : undefined}
        aria-labelledby={mode === 'interactive' ? ariaLabelledby : undefined}
        aria-valuemin={mode === 'interactive' ? min : undefined}
        aria-valuemax={mode === 'interactive' ? max : undefined}
        aria-valuenow={mode === 'interactive' && localScore != null ? localScore : undefined}
        aria-valuetext={
          mode === 'interactive' && localScore != null
            ? `${localScore} out of ${max}${bucket ? `, ${bucket.label}` : ''}`
            : mode === 'interactive'
              ? 'No score selected'
              : undefined
        }
        tabIndex={mode === 'interactive' ? 0 : undefined}
        onKeyDown={mode === 'interactive' ? handleKeyDown : undefined}
        style={{
          touchAction: 'none',
          cursor: mode === 'interactive' ? 'pointer' : undefined,
        }}
      >
       <div ref={trackRef} className="relative">
        {/* Gradient track */}
        <div
          data-testid={anchor('-track')}
          className="h-1.5 rounded-full w-full"
          style={{
            background: gradient,
            opacity: 1,
          }}
        />

        {/* Active fill layer — interactive mode */}
        {mode === 'interactive' && hasPlaced && localScore != null && (
          <motion.div
            className="absolute top-0 left-2 h-1.5 rounded-full overflow-hidden"
            style={{ maxWidth: 'calc(100% - 16px)' }}
            initial={false}
            animate={{ width: `${scorePercent}%` }}
            // While dragging, the fill must track the pointer 1:1 — a spring
            // here makes it visibly lag the thumb ("not reactive"). Spring is
            // kept only for tap/keyboard placement where the travel reads as
            // a deliberate settle.
            transition={
              isDragging ? { duration: 0 } : { type: 'spring', stiffness: 120, damping: 15 }
            }
          >
            <div
              data-testid={anchor('-fill')}
              className="h-full"
              style={{
                background: gradient,
                width: scorePercent > 0 ? `${10000 / scorePercent}%` : '100%',
              }}
            />
          </motion.div>
        )}

        {/* Shared positioning rail for thumb + numbers — same coordinate space */}
        <div
          className="absolute"
          style={{ top: 0, left: '8px', width: 'calc(100% - 16px)', height: 0 }}
        >
          {/* Marker (thumb) — interactive mode */}
          {mode === 'interactive' && hasPlaced && localScore != null && (
            <motion.div
              className="absolute"
              style={{
                top: '-9px',
                width: '22px',
                height: '22px',
                marginLeft: '-11px',
              }}
              initial={{ left: animateEntrance ? '0%' : `${scorePercent}%` }}
              animate={{
                left: `${scorePercent}%`,
                scale: isDragging ? 1.3 : 1,
              }}
              transition={{
                // Instant follow during an active drag (the spring lagged the
                // pointer); spring only for tap/keyboard placement.
                left: isDragging
                  ? { duration: 0 }
                  : { type: 'spring', stiffness: 80, damping: 12, mass: 0.8 },
                scale: { duration: 0.15 },
              }}
            >
              {/* Touch target (44px) */}
              <div
                data-testid={anchor('-thumb-hit')}
                className="absolute inset-0 -m-[11px]"
                style={{ width: '44px', height: '44px' }}
              />
              {/* Visual marker */}
              <div
                data-testid={anchor('-thumb')}
                className="w-[22px] h-[22px] rounded-full bg-white flex items-center justify-center"
                style={{
                  border: `3px solid ${bucketColor ?? '#9CA3AF'}`,
                  boxShadow: isDragging
                    ? `0 0 0 4px color-mix(in srgb, ${bucketColor ?? '#9CA3AF'} 20%, transparent), 0 2px 8px rgba(0,0,0,0.15)`
                    : '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <div
                  data-testid={anchor('-thumb-dot')}
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: bucketColor ?? '#9CA3AF' }}
                />
              </div>
            </motion.div>
          )}

          {/* Number markers — same positioning rail as thumb */}
          {numbers.map((n) => {
            const isActive = mode === 'interactive' && localScore === n;
            const numBucketHit = getBucketWithIndex(n, buckets);
            const numBucketColor = numBucketHit
              ? resolveBucketColor(numBucketHit.bucket, numBucketHit.index, bucketCount)
              : null;
            const isThinned = shouldThinNumbers && n !== min && n !== max && n !== midpoint;
            const pct = scoreToPercent(n, min, max);

            return (
              <button
                key={n}
                type="button"
                data-testid={anchor(`-marker-${n}`)}
                className={`absolute whitespace-nowrap font-body tabular-nums transition-all duration-200 ${
                  isThinned ? 'hidden min-[360px]:block' : ''
                } ${
                  mode === 'interactive'
                    ? 'cursor-pointer hover:opacity-80'
                    : 'cursor-default'
                }`}
                style={{
                  top: '18px',
                  left: `${pct}%`,
                  fontSize: '11px',
                  color: isActive && markerSettled ? (numBucketColor ?? '#6B7280') : '#9CA3AF',
                  fontWeight: isActive && markerSettled ? 600 : 400,
                  transform: `translateX(-50%)${isActive && markerSettled ? ' scale(1.2)' : ''}`,
                }}
                onClick={() => {
                  if (mode === 'interactive') {
                    updateScore(n);
                    commitScore(n, 'number_tap');
                  }
                }}
                tabIndex={-1}
                aria-hidden="true"
              >
                {n}
              </button>
            );
          })}
        </div>
       </div>{/* end inner track box (trackRef) */}
      </div>

      {/* Spacer for number markers below the track */}
      <div style={{ height: '32px' }} />

     </div>{/* end dimmed wrapper */}

      {/* Endpoint labels — outside dimmed wrapper so they stay fully readable */}
      <div className="flex justify-between px-2 mt-1">
        <span data-testid={anchor('-min-label')} className="font-body text-[11px] text-[#9CA3AF]">
          {min_label}
        </span>
        <span data-testid={anchor('-max-label')} className="font-body text-[11px] text-[#9CA3AF]">
          {max_label}
        </span>
      </div>

      {/* Hint text — interactive mode */}
      <AnimatePresence>
        {mode === 'interactive' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 1.2 }}
            className="flex items-center justify-center gap-2 mt-4"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-[#9CA3AF]">
              <path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span data-testid={anchor('-hint')} className="font-body text-xs text-[#9CA3AF]">
              {hasPlaced ? 'Drag to adjust your score' : 'Place your score on the scale'}
            </span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-[#9CA3AF]">
              <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
